import React, { useState } from 'react'
import Card from '@/components/common/Card'
import { Button } from '../ui/button';
import { API_URL } from '@/api/constant'
import type { IEmployee, IOAuthClient } from '@/interface/employeeOnBoard'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import {
    Mail,
    Phone,
    Shield,
    MapPin,
    Briefcase,
    CreditCard,
    Heart,
    User,
    AppWindow,
    History,
    FileText,
    ExternalLink,
    Eye,
    Files,
    X,
    IdCard,
    GraduationCap,
    FolderOpen,
    FileImage
} from 'lucide-react'

interface EmployeeProfileDetailViewProps {
    employee: IEmployee | any;
}

export const InfoItem: React.FC<{ label: string; value?: string | number | null | boolean }> = ({ label, value }) => {
    let displayValue: string | number = '—';
    if (value !== null && value !== undefined && value !== '') {
        displayValue = String(value);
    }
    return (
        <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
            <p className="text-sm font-medium text-foreground">{displayValue}</p>
        </div>
    );
};

export const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    try {
        const parsed = new Date(dateStr);
        if (isNaN(parsed.getTime())) return String(dateStr);
        return parsed.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return String(dateStr);
    }
};

const getGenderLabel = (gender: any) => {
    if (gender === 0 || gender === '0') return 'Male';
    if (gender === 1 || gender === '1') return 'Female';
    if (gender === 2 || gender === '2') return 'Other';
    if (typeof gender === 'string' && gender.trim()) return gender;
    return '—';
};

const getMaritalStatusLabel = (status: any) => {
    if (status === 1 || status === '1' || status === true) return 'Married';
    if (status === 0 || status === '0' || status === false) return 'Unmarried';
    if (status === '2') return 'Divorced';
    if (status === '3') return 'Widower';
    if (status === '4') return 'Widow';
    if (typeof status === 'string' && status.trim()) return status;
    return '—';
};

export const getMediaUrl = (url?: string | null) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}/${url.replace(/\\/g, '/').replace(/^\/+/, '')}`;
};

const isImageFile = (url?: string | null) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(url);
};

const isPdfFile = (url?: string | null) => {
    if (!url) return false;
    return /\.pdf(\?.*)?$/i.test(url);
};

const EmployeeProfileDetailView: React.FC<EmployeeProfileDetailViewProps> = ({ employee }) => {
    const [previewImage, setPreviewImage] = useState<{ url: string; title: string } | null>(null);
    const userDetail = employee?.userDetail || {};

    const rawPhoto = employee?.profilePhoto;
    const photoUrl = getMediaUrl(rawPhoto);

    const hasAssignedApps = Array.isArray(employee?.oauthClients) && employee.oauthClients.length > 0;
    const hasPreviousExp = Boolean(
        userDetail?.previousCompanyName ||
        userDetail?.previousDesignationName ||
        userDetail?.previousFromDate
    );

    // Collect all documents from documents array and direct document keys
    const rawDocuments: Array<{
        id?: string;
        name: string;
        type?: string;
        url: string;
        keyName?: string;
    }> = [];

    // 1. From employee.documents or userDetail.documents array
    const docArray = (employee?.documents && Array.isArray(employee.documents) ? employee.documents : [])
        .concat(userDetail?.documents && Array.isArray(userDetail.documents) ? userDetail.documents : []);

    docArray.forEach((doc: any) => {
        if (doc && doc.url && typeof doc.url === 'string') {
            rawDocuments.push({
                id: doc.id,
                name: doc.name || doc.keyName || 'Document',
                type: doc.type || 'DOCUMENT',
                url: doc.url,
                keyName: doc.keyName,
            });
        }
    });

    // 2. Direct document keys fallback (if not already present)
    const fixedDocMap: Record<string, { name: string; type: string }> = {
        aadharFront: { name: 'Aadhar Card Front', type: 'PERSONAL' },
        aadharBack: { name: 'Aadhar Card Back', type: 'PERSONAL' },
        panFront: { name: 'PAN Card', type: 'PERSONAL' },
        drivingLicense: { name: 'Driving License', type: 'PERSONAL' },
        passport: { name: 'Passport', type: 'PERSONAL' },
        marriageCertificate: { name: 'Marriage Certificate', type: 'PERSONAL' },
        currentAddressProof: { name: 'Current Address Proof', type: 'PERSONAL' },
        permanentAddressProof: { name: 'Permanent Address Proof', type: 'PERSONAL' },
        sscDoc: { name: 'SSC Marksheet', type: 'EDUCATION' },
        hscDoc: { name: 'HSC Marksheet', type: 'EDUCATION' },
        graduationDoc: { name: 'Graduation Marksheet', type: 'EDUCATION' },
        postGraduationDoc: { name: 'Post Graduation Marksheet', type: 'EDUCATION' },
        mastersDoc: { name: 'Masters Marksheet', type: 'EDUCATION' },
        degreeDoc: { name: 'Degree Certificate', type: 'EDUCATION' },
    };

    Object.entries(fixedDocMap).forEach(([key, meta]) => {
        const val = userDetail?.[key] || employee?.[key];
        if (val && typeof val === 'string' && val.trim()) {
            const alreadyExists = rawDocuments.some((d) => d.url === val || d.keyName === key);
            if (!alreadyExists) {
                rawDocuments.push({
                    name: meta.name,
                    type: meta.type,
                    url: val,
                    keyName: key,
                });
            }
        }
    });

    // Deduplicate by URL
    const uniqueDocs = rawDocuments.filter((doc, idx, arr) =>
        arr.findIndex((d) => d.url === doc.url) === idx
    );

    const personalDocumentKeys = [
        'aadharFront',
        'aadharBack',
        'panFront',
        'drivingLicense',
        'passport',
        'marriageCertificate',
        'currentAddressProof',
        'permanentAddressProof',
    ];

    const educationDocumentKeys = [
        'sscDoc',
        'hscDoc',
        'graduationDoc',
        'postGraduationDoc',
        'mastersDoc',
        'degreeDoc',
    ];

    const getDocumentCategory = (doc: { name: string; type?: string; keyName?: string }) => {
        const keyName = doc.keyName?.toLowerCase() || '';
        const type = doc.type?.toLowerCase() || '';
        const name = doc.name?.toLowerCase() || '';

        if (
            type.includes('education') ||
            educationDocumentKeys.some((key) => key.toLowerCase() === keyName) ||
            /(ssc|hsc|graduation|degree|masters|marksheet|academic|education)/i.test(name)
        ) {
            return 'education';
        }

        if (
            type.includes('personal') ||
            personalDocumentKeys.some((key) => key.toLowerCase() === keyName) ||
            /(aadhar|aadhaar|pan|passport|license|address|marriage|identity)/i.test(name)
        ) {
            return 'personal';
        }

        return 'other';
    };

    const documentGroups = [
        {
            id: 'personal',
            title: 'Personal Documents',
            description: 'Identity, address and KYC proofs',
            icon: IdCard,
            badgeClass: 'bg-indigo-50 text-indigo-700 border-indigo-100',
            iconClass: 'bg-indigo-50 text-indigo-600 border-indigo-100',
            docs: uniqueDocs.filter((doc) => getDocumentCategory(doc) === 'personal'),
        },
        {
            id: 'education',
            title: 'Education Documents',
            description: 'Marksheets, degrees and certificates',
            icon: GraduationCap,
            badgeClass: 'bg-sky-50 text-sky-700 border-sky-100',
            iconClass: 'bg-sky-50 text-sky-600 border-sky-100',
            docs: uniqueDocs.filter((doc) => getDocumentCategory(doc) === 'education'),
        },
        {
            id: 'other',
            title: 'Other Files',
            description: 'Supporting and miscellaneous uploads',
            icon: FolderOpen,
            badgeClass: 'bg-amber-50 text-amber-700 border-amber-100',
            iconClass: 'bg-amber-50 text-amber-600 border-amber-100',
            docs: uniqueDocs.filter((doc) => getDocumentCategory(doc) === 'other'),
        },
    ].filter((group) => group.docs.length > 0);

    const imageDocsCount = uniqueDocs.filter((doc) => isImageFile(doc.url)).length;
    const pdfDocsCount = uniqueDocs.filter((doc) => isPdfFile(doc.url)).length;
    const [openDocumentSections, setOpenDocumentSections] = useState<string[]>(['personal', 'education', 'other']);

    return (
        <div className='grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 items-start'>

            <div className="space-y-6">
                {/* Header Profile Card */}
                <Card>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                        <div className="relative">
                            {photoUrl ? (
                                <img
                                    src={photoUrl}
                                    alt={employee?.name || 'Employee Profile'}
                                    className="w-24 h-24 rounded-2xl object-cover border-2 border-primary/20 shadow-lg"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/20 flex items-center justify-center shadow-lg">
                                    <span className="text-3xl font-bold text-primary">
                                        {employee?.name?.charAt(0)?.toUpperCase() || 'E'}
                                    </span>
                                </div>
                            )}
                            <span
                                className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white shadow-sm ${employee?.status === 'current' ? 'bg-emerald-500' : 'bg-slate-400'
                                    }`}
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <h2 className="text-2xl font-bold text-foreground">{employee?.name || '—'}</h2>
                            <p className="text-sm text-muted-foreground mt-0.5">
                                {employee?.designationName || employee?.designation?.name || '—'}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Mail className="w-3.5 h-3.5 text-primary/70" /> {employee?.email || '—'}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <Phone className="w-3.5 h-3.5 text-primary/70" /> {employee?.phone || '—'}
                                </span>
                                {(employee?.designation?.name || employee?.role?.name || employee?.type !== undefined) && (
                                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                                        <Shield className="w-3.5 h-3.5 text-primary/70" />
                                        {employee?.designation?.name || employee?.role?.name || (employee?.type === 1 ? 'Admin' : 'Staff')}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                            <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${employee?.status === 'current'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-slate-50 text-slate-600 border border-slate-200'
                                    }`}
                            >
                                {employee?.status || '—'}
                            </span>
                            <span className="text-xs text-muted-foreground font-mono">{employee?.employeeCode || ''}</span>
                        </div>
                    </div>
                </Card>

                {/* Personal Information */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <User className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Personal Information</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        <InfoItem label="Full Name" value={employee?.name} />
                        <InfoItem label="Email" value={employee?.email} />
                        <InfoItem label="Phone" value={employee?.phone} />
                        {employee?.secondaryPhone && (
                            <InfoItem label="Secondary Phone" value={employee?.secondaryPhone} />
                        )}
                        {employee?.personalEmail && (
                            <InfoItem label="Personal Email" value={employee?.personalEmail} />
                        )}
                        <InfoItem label="Gender" value={getGenderLabel(employee?.gender)} />
                        <InfoItem label="Date of Birth" value={formatDate(employee?.dob)} />
                        <InfoItem label="Marital Status" value={getMaritalStatusLabel(employee?.isMarried)} />
                        <InfoItem label="Father's Name" value={employee?.fatherName || userDetail?.fatherName} />
                        <InfoItem label="Mother's Name" value={employee?.motherName || userDetail?.motherName} />
                        <InfoItem label="Blood Group" value={employee?.bloodGroup} />
                        {employee?.category && (
                            <InfoItem label="Category" value={employee?.category} />
                        )}
                    </div>
                </Card>

                {/* Employment Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Briefcase className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Employment Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        <InfoItem label="Employee Code" value={employee?.employeeCode} />
                        <InfoItem label="Department" value={employee?.department?.name || '—'} />
                        <InfoItem label="Designation" value={employee?.designation?.name || employee?.designationName || '—'} />
                        <InfoItem label="Branch" value={employee?.branch?.name || employee?.site?.name || '—'} />
                        <InfoItem label="Site" value={employee?.site?.name || '—'} />
                        <InfoItem label="Campus" value={employee?.campus?.name || employee?.site?.campuses?.[0]?.name || '—'} />
                        {employee?.shift?.name && (
                            <InfoItem label="Shift" value={employee?.shift?.name} />
                        )}
                        {employee?.teamLead?.name && (
                            <InfoItem label="Team Lead" value={employee?.teamLead?.name} />
                        )}
                        <InfoItem label="Joining Date" value={formatDate(employee?.joiningDate)} />
                        <InfoItem label="Status" value={employee?.status} />
                    </div>
                </Card>



                {/* Emergency Contact */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Phone className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Emergency Contact</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        <InfoItem label="Contact Person" value={employee?.ecpName || userDetail?.ecpName} />
                        <InfoItem label="Contact Number" value={employee?.ecpPhone || userDetail?.ecpPhone} />
                        <InfoItem
                            label="Relation"
                            value={userDetail?.ecpRelation || userDetail?.ecpContactRelation || '—'}
                        />
                    </div>
                </Card>

                {/* Address Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <MapPin className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Address Details</h3>
                    </div>

                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                        Present Address
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mb-6">
                        <InfoItem label="Present Address" value={userDetail?.address || employee?.address} />
                        {userDetail?.secondaryAddress && (
                            <InfoItem label="Present Secondary Address" value={userDetail?.secondaryAddress} />
                        )}
                        <InfoItem label="State" value={userDetail?.state || employee?.state} />
                        <InfoItem label="City" value={userDetail?.city || employee?.city} />
                        <InfoItem label="Pin Code" value={userDetail?.pinCode || employee?.pinCode} />
                    </div>

                    <div className="border-t border-border/50 pt-5">
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                            Permanent Address
                            {userDetail?.isSameAsPermanentAddress && (
                                <span className="ml-2 text-xs font-normal text-primary lowercase">
                                    (same as present)
                                </span>
                            )}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                            <InfoItem
                                label="Permanent Address"
                                value={userDetail?.permanentAddress || employee?.permanentAddress}
                            />
                            {userDetail?.secondaryPermanentAddress && (
                                <InfoItem
                                    label="Secondary Permanent Address"
                                    value={userDetail?.secondaryPermanentAddress}
                                />
                            )}
                            <InfoItem
                                label="State"
                                value={userDetail?.permanentState || employee?.permanentState}
                            />
                            <InfoItem
                                label="City"
                                value={userDetail?.permanentCity || employee?.permanentCity}
                            />
                            <InfoItem
                                label="Pin Code"
                                value={userDetail?.permanentPinCode || employee?.permanentPinCode}
                            />
                        </div>
                    </div>
                </Card>

                {/* ID & Documents Info */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Shield className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">ID & Identification Numbers</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        <InfoItem label="Aadhar Number" value={userDetail?.aadharNumber || employee?.aadharNumber} />
                        <InfoItem label="PAN Number" value={userDetail?.panNumber || employee?.panNumber} />
                        <InfoItem label="UAN Number" value={userDetail?.uanNumber || employee?.uanNumber} />
                        <InfoItem label="ESIC Number" value={userDetail?.esicNumber || employee?.esicNumber} />
                        <InfoItem label="PF Number" value={userDetail?.pfNumber || employee?.pfNumber} />
                        <InfoItem label="Insurance Number" value={userDetail?.insuranceNumber || employee?.insuranceNumber} />
                        <InfoItem
                            label="Driving License"
                            value={userDetail?.drivingLicenseNumber || employee?.drivingLicenseNumber}
                        />
                    </div>
                </Card>


                {/* Bank Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <CreditCard className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Bank Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        <InfoItem label="Bank Name" value={userDetail?.bankName || employee?.bankName} />
                        <InfoItem label="Account Number" value={userDetail?.accountNumber || employee?.accountNumber} />
                        <InfoItem label="Account Holder" value={userDetail?.accountHolderName || employee?.accountHolderName} />
                        <InfoItem label="IFSC/RTGS Code" value={userDetail?.ifscCode || employee?.ifscCode} />
                        <InfoItem label="Branch Address" value={userDetail?.branchAddress || employee?.branchAddress} />
                    </div>
                </Card>

                {/* Nominee Details */}
                <Card>
                    <div className="flex items-center gap-2 mb-5">
                        <Heart className="w-5 h-5 text-primary" />
                        <h3 className="font-display text-base font-semibold">Nominee Details</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        <InfoItem label="Nominee Name" value={userDetail?.nomineeName || employee?.nomineeName} />
                        <InfoItem label="Relation" value={userDetail?.nomineeRelation || employee?.nomineeRelation} />
                        <InfoItem label="Phone" value={userDetail?.nomineePhone || employee?.nomineePhone} />
                        <InfoItem
                            label="Date of Birth"
                            value={formatDate(userDetail?.nomineeDob || employee?.nomineeDob)}
                        />
                        {(userDetail?.nomineeEmail || employee?.nomineeEmail) && (
                            <InfoItem label="Nominee Email" value={userDetail?.nomineeEmail || employee?.nomineeEmail} />
                        )}
                        {(userDetail?.nomineeAddress || employee?.nomineeAddress) && (
                            <InfoItem label="Nominee Address" value={userDetail?.nomineeAddress || employee?.nomineeAddress} />
                        )}
                        {(userDetail?.nomineePercentage || employee?.nomineePercentage) && (
                            <InfoItem
                                label="Nominee Share (%)"
                                value={`${userDetail?.nomineePercentage || employee?.nomineePercentage}%`}
                            />
                        )}
                    </div>
                </Card>

                {/* Previous Work Experience (if available) */}
                {hasPreviousExp && (
                    <Card>
                        <div className="flex items-center gap-2 mb-5">
                            <History className="w-5 h-5 text-primary" />
                            <h3 className="font-display text-base font-semibold">Previous Work Experience</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                            <InfoItem label="Company Name" value={userDetail?.previousCompanyName} />
                            <InfoItem label="Designation" value={userDetail?.previousDesignationName} />
                            <InfoItem label="From Date" value={formatDate(userDetail?.previousFromDate)} />
                            <InfoItem label="To Date" value={formatDate(userDetail?.previousToDate)} />
                            <InfoItem
                                label="Current Company"
                                value={userDetail?.currentCompany ? 'Yes' : 'No'}
                            />
                        </div>
                    </Card>
                )}

                {/* Image Preview Modal */}
                {previewImage && (
                    <div
                        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setPreviewImage(null)}
                    >
                        <div
                            className="relative max-w-4xl max-h-[90vh] bg-card rounded-2xl overflow-hidden shadow-2xl border border-border flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card/90">
                                <h3 className="text-sm font-semibold text-foreground truncate">{previewImage.title}</h3>
                                <button
                                    type="button"
                                    onClick={() => setPreviewImage(null)}
                                    className="p-1 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-3 bg-black/5 dark:bg-black/40 flex items-center justify-center overflow-auto max-h-[calc(90vh-100px)]">
                                <img
                                    src={previewImage.url}
                                    alt={previewImage.title}
                                    className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-sm"
                                />
                            </div>
                            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-border bg-card">
                                <a
                                    href={previewImage.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" /> Open in New Tab
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className='space-y-6'>
                {/* Uploaded Documents Gallery / Files */}
                <Card className="lg:sticky lg:top-6 overflow-hidden p-0">
                    <div className="border-b border-border/60 py-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 shadow-sm">
                                    <Files className="w-5 h-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-display text-base font-semibold text-foreground">Uploaded Documents</h3>
                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                        Gallery view for employee files
                                    </p>
                                </div>
                            </div>
                            {uniqueDocs.length > 0 && (
                                <span className="shrink-0 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                                    {uniqueDocs.length} {uniqueDocs.length === 1 ? 'File' : 'Files'}
                                </span>
                            )}
                        </div>

                        {uniqueDocs.length > 0 && (
                            <div className="mt-4 grid grid-cols-3 gap-2">
                                <div className="rounded-lg border border-border/70 bg-card/80 px-3 py-2 shadow-xs">
                                    <p className="text-base font-bold text-foreground">{uniqueDocs.length}</p>
                                    <p className="text-[11px] font-medium text-muted-foreground">Total</p>
                                </div>
                                <div className="rounded-lg border border-border/70 bg-card/80 px-3 py-2 shadow-xs">
                                    <p className="text-base font-bold text-foreground">{imageDocsCount}</p>
                                    <p className="text-[11px] font-medium text-muted-foreground">Images</p>
                                </div>
                                <div className="rounded-lg border border-border/70 bg-card/80 px-3 py-2 shadow-xs">
                                    <p className="text-base font-bold text-foreground">{pdfDocsCount}</p>
                                    <p className="text-[11px] font-medium text-muted-foreground">PDFs</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {uniqueDocs.length > 0 ? (
                        <Accordion
                            type="multiple"
                            value={openDocumentSections}
                            onValueChange={setOpenDocumentSections}
                            className="space-y-3 pt-4"
                        >
                            {documentGroups.map((group) => {
                                const GroupIcon = group.icon;

                                return (
                                    <AccordionItem
                                        key={group.id}
                                        value={group.id}
                                        className="overflow-hidden rounded-xl border border-border/70 bg-background/60 shadow-xs transition-all hover:border-primary/30"
                                    >
                                        <AccordionTrigger className="rounded-none px-3 py-3 hover:bg-accent/50 hover:no-underline">
                                            <div className="mr-3 flex min-w-0 flex-1 items-center gap-3 text-left">
                                                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${group.iconClass}`}>
                                                    <GroupIcon className="h-4.5 w-4.5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-semibold text-foreground">{group.title}</p>
                                                    <p className="truncate text-xs text-muted-foreground">{group.description}</p>
                                                </div>
                                            </div>
                                            <span className={`mr-3 shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${group.badgeClass}`}>
                                                {group.docs.length}
                                            </span>
                                        </AccordionTrigger>

                                        <AccordionContent className="h-auto px-3 pb-3 pt-0 [&_a]:no-underline">
                                            <div className="space-y-3 pt-1">
                                                {group.docs.map((doc, idx) => {
                                                    const fullUrl = getMediaUrl(doc.url);
                                                    const isImg = isImageFile(doc.url);
                                                    const isPdf = isPdfFile(doc.url);
                                                    const fileTypeLabel = isImg ? 'Image file' : isPdf ? 'PDF document' : 'Document file';

                                                    return (
                                                        <div
                                                            key={doc.id || `${doc.name}-${idx}`}
                                                            className="group/doc overflow-hidden rounded-xl border border-border/70 bg-card shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/35 hover:shadow-md"
                                                        >
                                                            <div className="flex gap-3 p-3">
                                                                {isImg ? (
                                                                    <span
                                                                        className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-slate-100"
                                                                    >
                                                                        <img
                                                                            src={fullUrl}
                                                                            alt={doc.name}
                                                                            className="h-full w-full object-cover transition-transform duration-300 group-hover/thumb:scale-105"
                                                                        />

                                                                    </span>
                                                                ) : (
                                                                    <div className={`relative flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border ${isPdf ? 'border-rose-100 bg-rose-50 text-rose-600' : 'border-primary/15 bg-primary/10 text-primary'}`}>
                                                                        <FileText className="h-7 w-7" />
                                                                        <span className="absolute bottom-1 right-1 rounded bg-white/90 px-1 text-[9px] font-bold uppercase tracking-wide text-muted-foreground shadow-xs">
                                                                            {isPdf ? 'PDF' : 'DOC'}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <h4 className="truncate text-sm font-semibold leading-5 text-foreground" title={doc.name}>
                                                                            {doc.name}
                                                                        </h4>
                                                                        {isImg && (
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                onClick={() => setPreviewImage({ url: fullUrl, title: doc.name })}
                                                                                className=""
                                                                            >
                                                                                <Eye className="h-3.5 w-3.5" />
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                    <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                                                                        {isImg ? (
                                                                            <FileImage className="h-3.5 w-3.5 text-emerald-600" />
                                                                        ) : (
                                                                            <FileText className={`h-3.5 w-3.5 ${isPdf ? 'text-rose-500' : 'text-primary'}`} />
                                                                        )}
                                                                        <span>{fileTypeLabel}</span>
                                                                    </div>
                                                                    {doc.type && (
                                                                        <span className="mt-2 inline-flex max-w-full items-center rounded-full border border-border/70 bg-muted/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                                            <span className="truncate">{doc.type}</span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>


                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    ) : (
                        <div className="m-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-border/80 bg-muted/20 px-5 py-10 text-center">
                            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-border/70 bg-card shadow-xs">
                                <FileText className="h-7 w-7 text-muted-foreground/60" />
                            </div>
                            <p className="text-sm font-semibold text-foreground">No documents uploaded yet</p>
                            <p className="mt-1 max-w-52 text-xs text-muted-foreground">
                                Employee files will appear here once documents are added.
                            </p>
                        </div>
                    )}
                </Card>
                {/* Assigned Applications (if applicable) */}
                {hasAssignedApps && (
                    <Card>
                        <div className="flex items-center gap-2 mb-5">
                            <AppWindow className="w-5 h-5 text-primary" />
                            <h3 className="font-display text-base font-semibold">Assigned Applications</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {employee.oauthClients.map((client: IOAuthClient) => (
                                <span
                                    key={client.id || client.name}
                                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20"
                                >
                                    {client.name}
                                </span>
                            ))}
                        </div>
                    </Card>
                )}
            </div>

        </div>
    );
};

export default EmployeeProfileDetailView;
